/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2020, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import * as _ from 'underscore';
import * as moment from 'moment';
import { Injectable } from '@angular/core';
import { ProxyDataService } from '@app/core/http';
import { Logger, ModalService } from '@core/lib/services';
import { TranslateService } from '@ngx-translate/core';
import { appConstants } from '@app/common/app-constants';
import { WorkList } from '@app/data-model/worklist-navigator/work-list/work-list';
import { ToDoListService } from '@app/ui/common/work-list-navigator/to-do-list.service';
import { Count } from '@core/lib/components/to-do-count/count';
import { DataAccessService } from '@app/common/data-access.service';
import { CountUpdate } from '@app/data-model/count-update';
import { MessageService } from '@core/lib/services/message.service';
import { PinnedBarService } from '@app/ui/pinned-bar/pinned-bar.service';
import { AppConfigService } from '@app/common/app-config.service';
import { DatabaseList } from '@app/data-model/list/database-list';
import { DataTransformationService } from '@app/common/data-transformation.service';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { UserPrivilegesService } from '@app/common/user-privileges.service';
import { FilterUpdate } from '@app/data-model/filter-update';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { ToDoList, ToDoListId } from '@app/data-model/list/to-do-list';
import { Queue } from '@core/lib/data-structures/Queue';
import { WorkListHeaderDataService } from '@app/ui/common/work-list-header-data.service';
import { ToggleKnobService } from '@app/ui/common/toggle-knob-service';
import { SavedList } from '@app/data-model/list/saved-list';
import { TreeNode } from '@core/public-api';
import { LookupService } from '@wfm/record-management';

const log = new Logger('WorkListNavigatorService');
interface SavedFolderList {
  shared: TreeNode[];
  owned: TreeNode[];
}

@Injectable({
  providedIn: 'root'
})
export class WorkListNavigatorService {
  private defaultWorklistUpdateSubscriber: Subscription;

  private systemWorkLists: DatabaseList[] = [];
  private customWorkLists: DatabaseList[] = [];
  private toDoWorkLists: ToDoList[] = [];
  private savedWorkLists: SavedList[] = [];
  private savedWorkListsTree: SavedFolderList = { shared: [], owned: [] };

  private systemWorkListObserver: BehaviorSubject<DatabaseList[]> = new BehaviorSubject(this.systemWorkLists);
  private customWorkListObserver: BehaviorSubject<DatabaseList[]> = new BehaviorSubject(this.customWorkLists);
  private toDoWorkListObserver: BehaviorSubject<ToDoList[]> = new BehaviorSubject(this.toDoWorkLists);
  private savedWorkListObserver: BehaviorSubject<SavedList[]> = new BehaviorSubject(this.savedWorkLists);
  private folderListObserver: BehaviorSubject<SavedFolderList> = new BehaviorSubject(this.savedWorkListsTree);

  private linkedWorkLists = [];
  private toBeRemovedWorkListId;
  private customWorkListCountQueue: Queue = new Queue();
  private systemWorkListCountQueue: Queue = new Queue();
  private savedWorkListCountQueue: Queue = new Queue();

  activeWorklist;

  constructor(
    private dataService: ProxyDataService,
    private dataAccess: DataAccessService,
    private modalService: ModalService,
    private translate: TranslateService,
    private pinnedBarService: PinnedBarService,
    private toDoListService: ToDoListService,
    private appConfigService: AppConfigService,
    private messageService: MessageService,
    private userPreferenceService: UserPreferenceService,
    private dataTransformationService: DataTransformationService,
    private workListHeaderDataService: WorkListHeaderDataService,
    private userPrivilegesService: UserPrivilegesService,
    private toggleKnobService: ToggleKnobService,
    private lookupService: LookupService
  ) {}

  // Observers
  getSystemWorkLists(): Observable<DatabaseList[]> {
    return this.systemWorkListObserver.asObservable();
  }
  getCustomWorkLists(): Observable<DatabaseList[]> {
    return this.customWorkListObserver.asObservable();
  }
  getToDoWorkLists(): Observable<ToDoList[]> {
    return this.toDoWorkListObserver.asObservable();
  }
  getSavedWorkLists(): Observable<SavedList[]> {
    return this.savedWorkListObserver.asObservable();
  }
  getFolderList(): Observable<SavedFolderList> {
    return this.folderListObserver.asObservable();
  }

  // Exposed utility methods
  getToBeRemovedWorkListId() {
    return this.toBeRemovedWorkListId;
  }

  displayWorklist(workList: DatabaseList | ToDoList | SavedList) {
    if (workList && !workList.isWorklistDisabled) {
      this.toggleKnobService.openWorkloadManager();
      this.messageService.broadcast('list.display', { list: workList });
      this.messageService.broadcast('onWorklistChange');
    }
  }

  setPin(workList: DatabaseList | ToDoList | SavedList) {
    this.pinnedBarService.setPin(workList, !workList.isPinned);
  }

  deleteFilter(workListId: string) {
    this.messageService.broadcast('close.sideNav');
    const sub = this.modalService
      .openModal(this.translate.instant('filtering.deleteFilterMsg'), {
        headerContent: this.translate.instant('filtering.deleteFilterLabel'),
        disableHeaderCloseBtn: true,
        enableYesBtn: true,
        isDecisionModal: true,
        yesButtonLabel: this.translate.instant('label.clear'),
        noButtonLabel: this.translate.instant('label.cancel')
      })
      .afterClosed.subscribe(data => {
        sub.unsubscribe();
        if (data === 'YES') {
          this.workListHeaderDataService.deleteFilterAndSort(workListId);
          if (this.activeWorklist.id !== workListId) this.update(new FilterUpdate(workListId, undefined, undefined));
        }
      });
  }

  updateDefautList(data) {
    if (_.isEmpty(data) || data.isLinked) {
      this.postDefaultWorklistAction();
    } else {
      if (data.isCustom) {
        this.handleCustomWorklists([data]);
      } else {
        this.toDoWorkLists = this.toDoListService.prepareToDoWorkLists([data]);
        this.handleWorkLists([data], true);
      }

      const list = this.findWorkListById(data.id.toString());
      if (list.isToDo && list.isHidden) {
        this.postDefaultWorklistAction();
      } else {
        // Set default in preference
        this.userPreferenceService.setUserDefaultWorklist(data);
        this.messageService.broadcast('defaultWorklistLoaded', data);

        // Load worklist
        this.displayWorklist(list);
      }
      if (list && !list.isPinned) {
        this.setPin(list);
      }
    }
  }

  // Worklist initialization
  initializeWorkLists() {
    // Worklist subscribers
    this.registerWorklistChanges();
    this.prepareWorkLists();

    // Default worklists
    this.userPreferenceService.getDefaultUserWorklist().then(
      (data: any) => {
        this.updateDefautList(data);
      },
      () => {
        this.postDefaultWorklistAction();
      }
    );
  }

  private registerWorklistChanges() {
    this.messageService.on('list.display').subscribe(data => {
      if (data) {
        this.activeWorklist = data.list;
        if (this.toBeRemovedWorkListId && this.toBeRemovedWorkListId !== this.activeWorklist.worklistId) {
          this.removeWorkListById(this.toBeRemovedWorkListId);
          this.toBeRemovedWorkListId = '';
        }
      }
    });
    this.messageService.on('Worklist.ADD').subscribe(eppUpdate => {
      this.updateDatabaseWorklist(eppUpdate.rowId, eppUpdate.action);
    });
    this.messageService.on('Worklist.UPDATE').subscribe(eppUpdate => {
      this.updateDatabaseWorklist(eppUpdate.rowId, eppUpdate.action);
    });
    this.messageService.on('Worklist.ELIMINATE').subscribe(eppUpdate => {
      this.updateDatabaseWorklist(eppUpdate.rowId, eppUpdate.action);
    });
    this.messageService.on('refresh.myworklist').subscribe(worklistToBeSelected => {
      this.fetchCustomWorklists(worklistToBeSelected);
    });
    this.messageService.on('WorklistCountChange.UPDATE').subscribe(eppUpdate => {
      // To check and proceed if the event is not received for Unsigned or Attending Read worklist for current user
      if (this.isMyAttendingOrUnsignedWLOrOther(eppUpdate)) {
        eppUpdate.id = eppUpdate.rowId;
        const isInstituteSeparationEnabled = this.appConfigService.isInstituteSeparationEnabled();
        const accessibleInstitutes = Array.isArray(this.userPrivilegesService.institutes)
          ? this.userPrivilegesService.institutes
          : [];
        const update = new CountUpdate(
          eppUpdate,
          this.userPrivilegesService.userCanViewConfidentialPatient(),
          accessibleInstitutes,
          isInstituteSeparationEnabled
        );
        this.update(update);
        this.messageService.broadcast('worklistCountChange', update);
      }
    });
    this.messageService.on('displayDefaultWorkListByExternalApp').subscribe(() => {
      let list: any = this.userPreferenceService.getUserDefaultWorklist();
      if (list && list.id) {
        list = this.findWorkListById(list.id.toString());
        if (list) {
          this.displayWorklist(list);
        }
      } else {
        this.messageService.broadcast('displayNoDefaultWorkListError');
      }
    });
  }

  // To check if worklist count changes in recieved of current user
  private isMyAttendingOrUnsignedWLOrOther(eppUpdate) {
    if (eppUpdate.worklistName === 'attendingread' || eppUpdate.worklistName === 'reportstate') {
      return this.appConfigService.getUserId() == eppUpdate.radUsername;
    }
    return true;
  }

  // Subscribe to listener on default worklist preference change only if was not set
  private postDefaultWorklistAction() {
    this.messageService.broadcast('worklistLoaded.done');
    this.messageService.broadcast('noDefaultWorkList');
    this.defaultWorklistUpdateSubscriber = this.userPreferenceService
      .getDefaultWorklistUpdateObserver()
      .subscribe(worklistId => {
        if (worklistId) {
          const list = this.findWorkListById(worklistId);
          if (list) {
            this.displayWorklist(list);
            this.defaultWorklistUpdateSubscriber.unsubscribe();
          }
        }
      });
  }

  // System and Custom worklists
  private prepareWorkLists() {
    // default worklist load promise handler
    const workListLoadedSubscriber = this.messageService.on('worklistLoaded.done').subscribe(() => {
      workListLoadedSubscriber.unsubscribe();

      // Load remaining worklists
      this.fetchSystemWorklists();
      this.fetchCustomWorklists(null);
      this.fetchSavedWorklists(null);

      // Trigger for record management module
      if (this.appConfigService.getAppConfigByKey('cpacsVersion') === 'v8') {
        this.lookupService.initialize();
      }
    });
  }

  /**
   * Fetch Saved Folder
   */
  // Saved worklists API Call
  fetchSavedWorklists(worklistToBeSelected: SavedList | null): void {
    this.getWorkLists(appConstants.appURLs.allFolders)
      .then(data => {
        this.prepareFolders(data);
        this.folderListObserver.next(this.savedWorkListsTree);

        this.handleSavedWorkLists(this.savedWorkListsTree);
      })
      .then(() => {
        if (worklistToBeSelected) {
          const list = this.findWorkListById(worklistToBeSelected.id.toString());
          this.displayWorklist(list);
        }
      });
  }

  // Preprocessing TreeNodes
  private prepareFolders(data) {
    this.addIcons(undefined, data.shared, true);
    this.addIcons(undefined, data.owned, false);

    this.savedWorkListsTree.owned = _.sortBy(data.owned, folder => folder.name.toLowerCase());
    this.savedWorkListsTree.shared = _.sortBy(data.shared, folder => folder.name.toLowerCase());
  }

  private addIcons(parent: any, nodes: Array<any>, isSharedWithMe?: boolean) {
    if (nodes && nodes.length) {
      for (const node of nodes) {
        node.parent = parent;
        node.icon = node.shared || isSharedWithMe ? 'ico-share-16' : 'ico-folder-16';
        node.isSharedWithMe = isSharedWithMe;
        node.id = node.id.toString();
        node.isPinned = node.pinned;
        node.worklistId = node.id.toString();
        node.isOpen = true;

        if (node.children && node.children.length) {
          this.addIcons(node, node.children, isSharedWithMe);
        }
      }
    }
  }

  // SavedList creation
  private handleSavedWorkLists(data) {
    this.savedWorkLists = [];
    this.processSavedWorkListsTreeRecursively(data.shared);
    this.processSavedWorkListsTreeRecursively(data.owned);
    this.savedWorkListObserver.next(this.savedWorkLists);
    this.messageService.broadcast('savedWorkListsLoaded', this.savedWorkLists);

    const countBatchIds: string[] = [];
    _.each(this.savedWorkLists, (workList: SavedList) => {
      // Prioritize Pinned
      if (workList.isPinned) countBatchIds.splice(0, 0, workList.id);
      else countBatchIds.push(workList.id);
    });

    this.processWorklistCount(this.savedWorkListCountQueue, countBatchIds);
  }

  private processSavedWorkListsTreeRecursively(folderList, parent?: SavedList) {
    for (const folder of folderList as any[]) {
      let loadedWorklist: SavedList = _.find(this.savedWorkLists, { id: folder.id.toString() });
      if (loadedWorklist === undefined) {
        loadedWorklist = new SavedList(folder.id, folder.name, parent, {
          sharedByMe: folder.shared,
          sharedWithMe: folder.isSharedWithMe,
          descriptionMandatory: folder.descriptionMandatory,
          pinned: folder.pinned,
          layoutId: folder.layoutId
        });
        if (loadedWorklist.isPinned) {
          this.pinnedBarService.addWorkListToPinnedBar(loadedWorklist);
        }

        this.savedWorkLists.push(loadedWorklist);
      } else {
        loadedWorklist.displayName = folder.name;
        loadedWorklist.isSharedByMe = folder.shared;
        loadedWorklist.isSharedWithMe = folder.isSharedWithMe;
        loadedWorklist.isDescriptionMandatory = folder.descriptionMandatory;
        loadedWorklist.isPinned = folder.pinned;
        loadedWorklist.layoutId = folder.layoutId;
      }

      if (folder.children) {
        this.processSavedWorkListsTreeRecursively(folder.children, loadedWorklist);
      }
    }
  }

  // DatabaseList handlers
  private fetchSystemWorklists(): void {
    this.getWorkLists(appConstants.appURLs.worklists).then(systemWLs => {
      this.toDoWorkLists = this.toDoListService.prepareToDoWorkLists(systemWLs);
      this.handleWorkLists(systemWLs, true);
    });
  }

  private handleWorkLists(data: WorkList[], initRequired: boolean) {
    let systemWorkLists = data;

    if (initRequired) {
      this.rejectToDoWorkList(
        systemWorkLists,
        this.toDoWorkLists.map(list => parseInt(list.worklistId, 10))
      );
      systemWorkLists = _.reject(systemWorkLists, (worklist: WorkList) => {
        return !!this.findWorkListById(worklist.id.toString()) || this.isSearchWorklist(worklist);
      });
    }

    const countBatchIds = [];
    _.each(systemWorkLists, (workList: WorkList) => {
      this.processDatabaseList(workList, this.systemWorkLists);
      if (!workList.isGlobal) {
        // Prioritize Pinned
        if (workList.pinned) countBatchIds.splice(0, 0, workList.id.toString());
        else countBatchIds.push(workList.id.toString());
      }
    });

    this.updateWorklistDetailsLinkedParent();
    this.processWorklistCount(this.systemWorkListCountQueue, countBatchIds);

    this.systemWorkListObserver.next(this.systemWorkLists);
    this.toDoWorkListObserver.next(this.toDoWorkLists);
    this.messageService.broadcast('allWorklistRefresh', this.systemWorkLists);
  }

  private createDatabaseListFromWorklist(workList) {
    const isProtocolWorkList = workList.protocolWorklist && this.appConfigService.getProtocolEnabled();
    const databaseList = new DatabaseList(
      workList.id.toString(),
      workList.name,
      workList.pinned,
      isProtocolWorkList,
      workList.sortByPriority,
      workList.isLinked,
      workList.parentId.toString(),
      this.dataTransformationService.getFormattedDate(workList.worklistCreatedOn, 'MMM DD, YYYY'),
      workList.worklistUpdatedOn,
      workList.filterJson,
      workList.layoutId,
      workList.isCustom,
      workList.isGlobal
    );
    if (databaseList.isPinned) {
      this.pinnedBarService.addWorkListToPinnedBar(databaseList);
    }
    if (databaseList.isLinked) {
      this.linkedWorkLists.push({ id: workList.id, parent: workList.parentId });
    }
    return databaseList;
  }

  // CustomList handlers
  private fetchCustomWorklists(worklistToBeSelected: DatabaseList | null): void {
    this.getWorkLists(appConstants.appURLs.customWorklist)
      .then((customWLs: WorkList[]) => {
        this.handleCustomWorklists(customWLs);
      })
      .then(() => {
        if (worklistToBeSelected) {
          const list = this.findWorkListById(worklistToBeSelected.id.toString());
          this.displayWorklist(list);
        }
      });
  }

  private handleCustomWorklists(data: any[]) {
    if (!(data instanceof Array)) {
      log.warn('Data received from /myworklists/ endpoint was not an array.');
      return;
    }

    const countBatchIds = [];
    log.debug('Custom Worklists Length' + data.length);

    _.each(data, (workList: WorkList) => {
      this.processDatabaseList(workList, this.customWorkLists);

      // Prioritize Pinned
      if (workList.pinned) countBatchIds.splice(0, 0, workList.id.toString());
      else countBatchIds.push(workList.id.toString());
    });

    this.updateWorklistDetailsLinkedParent();
    this.processWorklistCount(this.customWorkListCountQueue, countBatchIds);

    this.customWorkListObserver.next(this.customWorkLists);
    this.messageService.broadcast('customWorklistRefresh', this.customWorkLists);
  }

  // DatabaseList processor
  private processDatabaseList(workList: WorkList, list: DatabaseList[]) {
    try {
      let loadedWorklist = _.find(list, { id: workList.id.toString() });
      if (loadedWorklist === undefined) {
        loadedWorklist = this.createDatabaseListFromWorklist(workList);
        if (workList.isGlobal) {
          loadedWorklist.count['isUnmonitoredWhenInactive'] = true;
        }
        if (workList.isCustom) {
          loadedWorklist.autoLaunchViewer = workList.viewerPreference === 1;
        }
        list.push(loadedWorklist);
      } else {
        if (workList.isCustom) {
          loadedWorklist.displayName = workList.name;
          loadedWorklist.protocolWorklist = workList.protocolWorklist;
          loadedWorklist.sortByPriority = workList.sortByPriority;
          loadedWorklist.autoLaunchViewer = workList.viewerPreference === 1;
        }
        loadedWorklist.updatedOn = workList.worklistUpdatedOn;
      }
    } catch (err) {
      const workListType = workList.isCustom ? 'Custom' : 'System';
      log.error(workListType + " Worklist Add/Update errored for '" + workList.name + "'");
    }
  }

  // LinkedList handlers
  private updateWorklistDetailsLinkedParent() {
    for (let i = 0; i < this.linkedWorkLists.length; i++) {
      const linkedList = this.findWorkListById(this.linkedWorkLists[i].id.toString());
      const linkedListParent = this.findWorkListById(this.linkedWorkLists[i].parent.toString());

      if (linkedList) {
        let isWorklistDisabled = true;
        let isParentUpdated, linkedParentDisplayName;

        if (linkedListParent && !_.isEqual(this.toBeRemovedWorkListId, linkedListParent.id)) {
          linkedListParent.linkedWorklists[linkedList.id] = { id: linkedList.id, displayName: linkedList.displayName };
          isWorklistDisabled = false;
          linkedParentDisplayName = linkedListParent.displayName;
          isParentUpdated = moment(linkedListParent.updatedOn).isAfter(linkedList.updatedOn);
        }

        // Update the datamodel
        linkedList.isWorklistDisabled = isWorklistDisabled;
        linkedList.linkedParentDisplayName = linkedParentDisplayName;
        linkedList.isParentUpdated = isParentUpdated;
      } else if (_.isUndefined(linkedList) && linkedListParent) {
        delete linkedListParent.linkedWorklists[this.linkedWorkLists[i].id.toString()];
      }
    }
  }

  // Count handlers
  private handleCounts(countUpdates: CountUpdate[]) {
    _.each(countUpdates, (countUpdate: CountUpdate) => {
      this.updateListCount(countUpdate);
    });

    if (countUpdates.length) {
      this.messageService.broadcast('list.count.update');
    }
  }

  private processWorklistCountQueue(queue: Queue) {
    if (queue.canDequeue()) {
      this.dataAccess.getDatabaseListCount(queue.dequeue()).then((countUpdates: CountUpdate[]) => {
        this.handleCounts(countUpdates);
        this.processWorklistCountQueue(queue);
      });
    }
  }

  private processWorklistCount(queue: Queue, countBatchIds: string[]) {
    if (countBatchIds.length > appConstants.workListConfig.worklistCountBlockSize) {
      const batches = _.chunk(countBatchIds, appConstants.workListConfig.worklistCountBlockSize);
      _.each(batches, (batch: string[]) => {
        queue.enqueue(batch);
      });
    } else if (countBatchIds.length) {
      queue.enqueue(countBatchIds);
    }

    this.processWorklistCountQueue(queue);
  }

  // Global Worklist ADD / DELETE Handlers
  private async updateDatabaseWorklist(rowId: string, action: string) {
    try {
      const data = await this.dataService.get('worklists?ids=' + rowId);
      const worklist = data[0];
      if (worklist !== undefined) {
        if (action === 'ADD') {
          this.handleWorkLists([worklist], true);
        } else if (action === 'UPDATE') {
          this.handleWorkLists([worklist], false);
          //Refresh the smart filter drop downs in header for active worklist
          /*if (this.appConfigService.getActiveList().id === rowId) {
            this.worklistHeaderDataService.prefetchFilters();
          }*/
          log.debug(
            'updateDatabaseWorklist -- action: ' +
              action +
              ', id: ' +
              rowId +
              ', name: ' +
              worklist.name +
              ', pinned: ' +
              worklist.pinned
          );
        }
      } else {
        if (action === 'UPDATE' || action === 'ELIMINATE') {
          this.checkForWorlistRemoval(rowId);
          log.debug('Worklist : ' + rowId + ': Navigator Service : ' + action);
        } else {
          log.debug('Worklist : ' + rowId + ': Navigator Service : ' + action + ' : Undefined Worklist');
        }
      }
    } catch (exception) {
      log.error(
        'updateDatabaseWorklist -- worklist id:' +
          rowId +
          ', action:' +
          action +
          '. Error: ' +
          JSON.stringify(exception)
      );
    }
  }

  /** Utility methods */
  // Fetch Worklists
  private getWorkLists(endPoint: string): Promise<any> {
    return new Promise(resolve => {
      this.dataService
        .get(endPoint)
        .then(
          data => {
            resolve(data);
          },
          error => {
            log.error(`Error Occurred while fetch from ${endPoint} endpoint. Error ==> ${JSON.stringify(error)}`);
            resolve(true);
          }
        )
        .catch(ex => {
          log.error(`Exception Occurred while fetch from ${endPoint} endpoint. Error ==> ${JSON.stringify(ex)}`);
          resolve(true);
        });
    });
  }

  // Find worklist
  findWorkListById(id: string) {
    return (
      _.find(this.customWorkLists, { id: id }) ||
      _.find(this.systemWorkLists, { id: id }) ||
      _.find(this.toDoWorkLists, { id: id }) ||
      _.find(this.toDoWorkLists, { worklistId: id }) ||
      _.find(this.savedWorkLists, { id: id })
    );
  }

  // Remove worklist
  checkForWorlistRemoval(worklistId: string): void {
    const worklist = this.findWorkListById(worklistId);
    if (worklist) {
      if (this.activeWorklist?.worklistId !== worklistId || worklist.isCustom === true)
        this.removeWorkListById(worklistId);
      else this.toBeRemovedWorkListId = worklistId;
    }
  }

  private removeWorkListById(id: string): void {
    let workListIndex = _.findIndex(this.systemWorkLists, { id: id });
    if (workListIndex > -1) {
      this.systemWorkLists.splice(workListIndex, 1);
      this.pinnedBarService.removeWorkListFromPinnedBarById(id);
      this.updateWorklistDetailsLinkedParent();
      this.systemWorkListObserver.next(this.systemWorkLists);
      this.messageService.broadcast('allWorklistRefresh', this.systemWorkLists);
      this.customWorkListObserver.next(this.customWorkLists);
      this.messageService.broadcast('customWorklistRefresh', this.customWorkLists);
      this.messageService.broadcast('worklist.deleted', id);
    } else {
      workListIndex = _.findIndex(this.customWorkLists, { id: id });
      if (workListIndex > -1) {
        this.customWorkLists.splice(workListIndex, 1);
        this.updateWorklistDetailsLinkedParent();
        this.pinnedBarService.removeWorkListFromPinnedBarById(id);
        this.customWorkListObserver.next(this.customWorkLists);
        this.messageService.broadcast('customWorklistRefresh', this.customWorkLists);
        this.messageService.broadcast('worklist.deleted', id);
      }
    }
  }

  // Update Worklist Count
  private updateListCount(countUpdate: CountUpdate) {
    const workList = this.findWorkListById(countUpdate.id);
    if (workList) {
      if (workList.count) {
        // already exists, don't mess with the isUnmonitored property
        workList.count.total = countUpdate.total;
        workList.count.stat = countUpdate.stat;
        workList.count.findings = countUpdate.findings;
      } else {
        // create a count object.
        workList.count = new Count(
          countUpdate.total,
          countUpdate.stat,
          countUpdate.findings,
          countUpdate.isUnmonitored
        );
      }
    }
  }

  // Check Worklist is of type Search
  private isSearchWorklist(worklist): boolean {
    return /^search results ordered by priority and study_dttm$/i.test(worklist.description);
  }

  // Filter ToDo worklists from the list
  private rejectToDoWorkList(data: WorkList[], toDoWorkListIds: number[]) {
    if (toDoWorkListIds && toDoWorkListIds.length > 0) {
      _.each(toDoWorkListIds, id => {
        const index = _.findIndex(data, { id });
        if (index > -1) {
          data.splice(index, 1);
        }
      });
    }
  }

  // Update the worklist Filter/Sort parameter and count
  update(listUpdateAction: CountUpdate | FilterUpdate) {
    if (!listUpdateAction) return;

    const workList = this.findWorkListById(listUpdateAction.id);
    if (workList) {
      if (listUpdateAction instanceof CountUpdate) {
        //if current list checksum is -1 and event checksum is null, we want to keep current checksum state
        if (!(workList.checksum === -1 && listUpdateAction.checksum === null)) {
          workList.checksum = listUpdateAction.checksum;
        }

        if (workList.filterData) {
          this.getFilteredCount(workList);
        } else {
          // If there is a monitored existing count and the incoming count is unmonitored, retain the existing count
          // Reasoning: This can occur when the worklist is not unmonitored, the negative count is received because of a count calculation error.
          const maintainExistingCount =
            workList.count && !workList.count.isUnmonitored && listUpdateAction.isUnmonitored;
          if (!maintainExistingCount) {
            workList.count = new Count(listUpdateAction.total, listUpdateAction.stat, listUpdateAction.findings);
            workList.count.isUnmonitored = listUpdateAction.isUnmonitored;
          }
          this.messageService.broadcast('list.count.update');
        }
        return true;
      } else if (listUpdateAction instanceof FilterUpdate) {
        workList.filterData = listUpdateAction.filterData;
        workList.sortData = listUpdateAction.sortData;
        this.getFilteredCount(workList);
        return true;
      }
    }
    return false;
  }

  private getFilteredCount(workList: DatabaseList | ToDoList | SavedList) {
    if (!isNaN(parseInt(workList.id)) || workList.isSavedList) {
      this.dataAccess.getDatabaseListCount(workList.id, workList.filterData).then((updates: CountUpdate[]) => {
        //updates is an array, but there's only one update in it at most
        if (updates.length) {
          this.updateListCount(updates[0]);
          this.messageService.broadcast('list.count.update');
        }
      });
    } else {
      // ids from todo worklists are text for example 'assigned, unsigned, attendingread'
      this.dataAccess.getTodoListCount(workList).then((updates: CountUpdate[]) => {
        //updates is an array with list of objects for assigned,unsigned, attendingread , but there's only one update.
        if (updates.length) {
          for (let i = 0; i < updates.length; ++i) {
            if (updates[i]) this.updateListCount(updates[i]);
          }
          this.messageService.broadcast('list.count.update');
        }
      });
    }
  }

  // Update the ToDo counts for 'assigned', 'unsigned', 'attendingread'
  findAndUpdateToDoCount() {
    [ToDoListId.ASSIGNED, ToDoListId.UNSIGNED, ToDoListId.ATTENDING].forEach((listId: string) => {
      const worklist = _.find(this.toDoWorkLists, { id: listId });
      if (worklist && worklist.filterData) this.getFilteredCount(worklist);
    });
  }

  // is ToDo counts for 'assigned', 'unsigned', 'attendingread' filtered
  isToDoFiltered(): boolean {
    let isFiltered = false;
    [ToDoListId.ASSIGNED, ToDoListId.UNSIGNED, ToDoListId.ATTENDING].forEach((listId: string) => {
      const worklist = _.find(this.toDoWorkLists, { id: listId });
      if (worklist && worklist.filterData) isFiltered = true;
    });
    return isFiltered;
  }
}
